import { createStringArraySelector, sectionSelectors, termSelectors } from "./base";
import { selectAuthorableSectionIds } from "./section";
import { selectSectionIdsForTerm } from "./terms";

import { compactMap } from "utils/collections";

/** Preserve `orderedIds` order while keeping only ids also present in `otherIds`. */
const orderedIdIntersection = (
  orderedIds: readonly string[],
  otherIds: readonly string[],
): string[] => {
  const otherIdSet = new Set(otherIds);
  return orderedIds.filter((id) => otherIdSet.has(id));
};

/** Unlinked section ids. */
export const selectUnlinkedSectionIds = createStringArraySelector(
  [sectionSelectors.selectAll],
  (sections): string[] =>
    compactMap(sections, (section) => (section.lms_section_id === null ? section.id : null)),
);

/** Unlinked authorable section ids (LMS link picker). */
export const selectLinkableSectionIds = createStringArraySelector(
  [selectAuthorableSectionIds, selectUnlinkedSectionIds],
  (authorableSectionIds, unlinkedSectionIds): string[] =>
    orderedIdIntersection(authorableSectionIds, unlinkedSectionIds),
);

/**
 * Term ids for linkable sections, in `termSelectors.selectIds` order.
 * Appends section-only term ids (missing from the terms slice) so Unassigned groups still render.
 */
export const selectLinkableTermIds = createStringArraySelector(
  [selectLinkableSectionIds, sectionSelectors.selectEntities, termSelectors.selectIds],
  (sectionIds, sectionEntities, termIds): string[] => {
    const termIdsFromSections = [
      ...new Set(
        compactMap(sectionIds, (sectionId) => sectionEntities[sectionId]?.term_id || null),
      ),
    ];
    const orderedKnownTermIds = orderedIdIntersection(termIds, termIdsFromSections);
    const knownTermIdSet = new Set(orderedKnownTermIds);
    return [
      ...orderedKnownTermIds,
      ...termIdsFromSections.filter((termId) => !knownTermIdSet.has(termId)),
    ];
  },
);

/** Linkable section ids for a term (authorable / full-title order). */
export const selectLinkableSectionIdsForTerm = createStringArraySelector(
  [selectLinkableSectionIds, selectSectionIdsForTerm],
  (linkableSectionIds, sectionIdsForTerm): string[] =>
    orderedIdIntersection(linkableSectionIds, sectionIdsForTerm),
);
